/**
 * In-memory DS-снимок (Document Server / R7) на строку FILEATTACHMENTS.
 *
 * **Зачем отдельно от UniData row:**
 * - bundle prepare (JWT, init-config, callbackUrl, `documentSessionId`) **не уходит** в `getData`/`getFullData`;
 * - фаза строки для бейджа и блокировок (`idle` → `editing` → `awaiting_ws` → `staged`);
 * - reuse DocEditor без лишнего HTTP prepare, пока снимок валиден.
 *
 * **Границы:** один store на `elementId` FILEATTACHMENTS; сброс при unmount элемента / promote / delete строки.
 * Корреляция строки — `lookupAttachmentSnapshot` (code:version, clientRowKey, documentSessionId, storage_uri).
 *
 * @see dev/contracts/attachments.md — DS-снимок, матрица бейджей
 * @see dev/tasks/WUI-344.md
 */
import type { R7DocEditorInitConfig } from '@/common/types/r7DocEditorInitConfig'
import type { FileItem } from '@/common/types/fileItem'
import { reactive } from 'vue'
import {
  ATTACHMENT_STORAGE_URI_FIELD_KEYS,
  ensureAttachmentClientRowKey,
  getAttachmentClientRowKey,
  getAttachmentDocumentSessionIdFromFields,
  getScalarFromFields,
  resolveAttachmentRowCodeVersion,
  rowAttachmentKey,
  type PlatformDownloadMeta,
} from '@/common/utils/fileAttachmentsUniData'
import { isDsAttachmentRowOpenBlocked } from '@/common/utils/dsAttachmentPhases'
import type { DsAttachmentPhase } from '@/common/utils/dsAttachmentPhases'

/** Полный in-memory снимок DS-цикла для одной строки ленты. */
export type DsAttachmentSnapshot = {
  documentKey: string
  storageUri: string
  documentUrl: string
  callbackUrl: string
  /** Browser JWT из prepare; только in-memory, не в UniData. Сбрасывается после WS 2/6. */
  token?: string
  /**
   * Init-config DocEditor от middleware (подписываемая часть + JWT).
   * После WS 2/6 **сбрасывается** → reopen = idempotent prepare по `documentSessionId`.
   */
  docEditorInitConfig?: R7DocEditorInitConfig
  /** Стабильный id DS-сессии (R9); не меняется при R5 (новый documentKey). */
  documentSessionId: string
  generation: number
  phase: DsAttachmentPhase
  dsDirty: boolean
  clientRowKey: string
  platformCode?: number
  platformVersion?: number
}

export type DsAttachmentRowState = {
  current: DsAttachmentSnapshot
  previous?: DsAttachmentSnapshot
}

/** Вход после `POST office-editor/prepare` или restore из download/sync-state. */
export type OfficePrepareSnapshotInput = {
  documentKey: string
  storageUri: string
  documentUrl: string
  callbackUrl: string
  token?: string
  /** При upsert без поля сохраняется из существующего снимка. */
  docEditorInitConfig?: R7DocEditorInitConfig
  documentSessionId: string
  generation?: number
  dsDirty?: boolean
  /** Если не задано — preserve prev phase (staged/awaiting_ws/editing не сбрасывать в idle). */
  phase?: DsAttachmentPhase
  clientRowKey: string
  platformCode?: number
  platformVersion?: number
}

/**
 * Ключ строки в Map снимков элемента.
 * Приоритет: `code:version` → `client:{__clientRowKey}`.
 */
export function dsAttachmentRowKey(item: FileItem): string {
  const fields = (item.fields ?? {}) as Record<string, unknown>
  const cv = resolveAttachmentRowCodeVersion(fields)
  if (cv && cv.code > 0) {
    return `${cv.code}:${cv.version}`
  }
  const clientRowKey = ensureAttachmentClientRowKey(item)
  return `client:${clientRowKey}`
}

type SnapshotLookupHit = { snapshot: DsAttachmentSnapshot; rowKey: string }

/**
 * Найти снимок по полям строки **без** мутации `item.fields`.
 * Важно после upload (client→code:version) и для WS по `documentSessionId`.
 */
function lookupAttachmentSnapshot(
  item: FileItem,
  byRowKey: Map<string, DsAttachmentRowState>,
): SnapshotLookupHit | undefined {
  const fields = (item.fields ?? {}) as Record<string, unknown>

  const platformKey = rowAttachmentKey(fields)
  if (platformKey) {
    const snapshot = byRowKey.get(platformKey)?.current
    if (snapshot) return { snapshot, rowKey: platformKey }
  }

  const clientKey = getAttachmentClientRowKey(fields)
  if (clientKey) {
    const rowKey = `client:${clientKey}`
    const snapshot = byRowKey.get(rowKey)?.current
    if (snapshot) return { snapshot, rowKey }
  }

  const sessionId = getAttachmentDocumentSessionIdFromFields(fields)
  if (sessionId) {
    for (const [rowKey, state] of byRowKey.entries()) {
      if (state.current.documentSessionId === sessionId) {
        return { snapshot: state.current, rowKey }
      }
    }
  }

  const storageUri = getScalarFromFields(fields, ...ATTACHMENT_STORAGE_URI_FIELD_KEYS)
  if (storageUri != null && String(storageUri).trim() !== '') {
    const uri = String(storageUri).trim()
    for (const [rowKey, state] of byRowKey.entries()) {
      if (state.current.storageUri === uri) {
        return { snapshot: state.current, rowKey }
      }
    }
  }

  return undefined
}

/** Фаза после merge meta download: не затирать staged/awaiting_ws. */
function resolvePhaseFromDownloadMeta(
  existing: DsAttachmentSnapshot | undefined,
  meta: PlatformDownloadMeta,
): DsAttachmentPhase | undefined {
  const prev = existing?.phase
  if (prev === 'staged' || prev === 'awaiting_ws') return prev
  if (meta.dsDirty) return 'staged'
  return prev
}

/**
 * Фабрика store снимков для одного FILEATTACHMENTS (`elementId`).
 * Reactive `Map` — чтение фазы в computed бейджа ленты.
 */
export function createDsAttachmentStore() {
  const byRowKey = reactive(new Map<string, DsAttachmentRowState>())

  /** Состояние по внутреннему ключу Map (отладка / тесты). */
  function getRowState(rowKey: string): DsAttachmentRowState | undefined {
    return byRowKey.get(rowKey)
  }

  /** Текущий снимок строки или `undefined` (фаза `none`). */
  function getSnapshot(item: FileItem): DsAttachmentSnapshot | undefined {
    return lookupAttachmentSnapshot(item, byRowKey)?.snapshot
  }

  /** DS-фаза строки; без снимка — `'none'`. */
  function getPhase(item: FileItem): DsAttachmentPhase {
    return getSnapshot(item)?.phase ?? 'none'
  }

  function hasSnapshot(item: FileItem): boolean {
    return getPhase(item) !== 'none'
  }

  /** Блок open/preview/delete по фазе (`awaiting_ws`, `error`, …). */
  function isOpenBlocked(item: FileItem): boolean {
    return isDsAttachmentRowOpenBlocked(getPhase(item))
  }

  /** Сохранить копию `current` в `previous` перед risky-операцией (reconcile). */
  function pushPrevious(item: FileItem): void {
    const hit = lookupAttachmentSnapshot(item, byRowKey)
    const key = hit?.rowKey ?? dsAttachmentRowKey(item)
    const row = byRowKey.get(key)
    if (!row?.current) return
    byRowKey.set(key, { current: row.current, previous: { ...row.current } })
  }

  /**
   * Создать/обновить снимок после prepare или platform download.
   * Open/prepare **не** сбрасывает staged/awaiting_ws/editing → idle.
   */
  function upsertFromPrepare(item: FileItem, input: OfficePrepareSnapshotInput): DsAttachmentSnapshot {
    const existing = lookupAttachmentSnapshot(item, byRowKey)
    const key = existing?.rowKey ?? dsAttachmentRowKey(item)
    const clientRowKey =
      input.clientRowKey
      || existing?.snapshot.clientRowKey
      || ensureAttachmentClientRowKey(item)
    const prevPhase = existing?.snapshot.phase
    const preservePhase: DsAttachmentPhase =
      input.phase
      ?? (prevPhase === 'staged'
        || prevPhase === 'awaiting_ws'
        || prevPhase === 'editing'
        || prevPhase === 'error'
        || prevPhase === 'idle'
        ? prevPhase
        : 'idle')
    const preserveDirty =
      input.dsDirty === true
      || (preservePhase === 'staged' && existing?.snapshot.dsDirty === true)
    const snapshot: DsAttachmentSnapshot = {
      documentKey: input.documentKey,
      storageUri: input.storageUri,
      documentUrl: input.documentUrl,
      callbackUrl: input.callbackUrl,
      token: input.token ?? existing?.snapshot.token,
      docEditorInitConfig: input.docEditorInitConfig ?? existing?.snapshot.docEditorInitConfig,
      documentSessionId: input.documentSessionId,
      generation: Math.max(input.generation ?? 0, existing?.snapshot.generation ?? 0),
      phase: preservePhase,
      dsDirty: preserveDirty,
      clientRowKey,
      platformCode: input.platformCode ?? existing?.snapshot.platformCode,
      platformVersion: input.platformVersion ?? existing?.snapshot.platformVersion,
    }
    const prev = byRowKey.get(key)
    byRowKey.set(key, { current: snapshot, previous: prev?.previous })
    return snapshot
  }

  /**
   * WS/syncState save ready (2/6): новый temp + documentKey (R5).
   * Сбрасывает JWT/init — reopen без prepare невозможен до idempotent prepare.
   */
  function applyWsStaged(
    item: FileItem,
    input: {
      documentKey: string
      storageUri: string
      documentUrl?: string
      callbackUrl?: string
    },
  ): DsAttachmentSnapshot | undefined {
    const hit = lookupAttachmentSnapshot(item, byRowKey)
    if (!hit?.snapshot) return undefined
    const { snapshot: current, rowKey: key } = hit
    const next: DsAttachmentSnapshot = {
      ...current,
      documentKey: input.documentKey,
      storageUri: input.storageUri,
      documentUrl: input.documentUrl ?? current.documentUrl,
      callbackUrl: input.callbackUrl ?? current.callbackUrl,
      documentSessionId: current.documentSessionId,
      generation: current.generation + 1,
      phase: 'staged',
      dsDirty: true,
      token: undefined,
      docEditorInitConfig: undefined,
    }
    const prev = byRowKey.get(key)
    byRowKey.set(key, { current: next, previous: prev?.previous })
    return next
  }

  /** Явная смена DS-фазы (lifecycle, WS error, restore preOpen). */
  function setPhase(item: FileItem, phase: DsAttachmentPhase): void {
    const hit = lookupAttachmentSnapshot(item, byRowKey)
    const key = hit?.rowKey ?? dsAttachmentRowKey(item)
    const row = byRowKey.get(key)
    if (!row?.current || row.current.phase === phase) return
    byRowKey.set(key, { current: { ...row.current, phase }, previous: row.previous })
  }

  /** Удалить снимок строки (delete на ленте, discard, resetAfterPromote). */
  function clearSnapshot(item: FileItem): void {
    const hit = lookupAttachmentSnapshot(item, byRowKey)
    if (hit) {
      byRowKey.delete(hit.rowKey)
      return
    }
    byRowKey.delete(dsAttachmentRowKey(item))
  }

  function clearAll(): void {
    byRowKey.clear()
  }

  /**
   * После promote на getFullData: middleware завершает DS-сессию.
   * Старый `documentSessionId` в снимке невалиден — нужен новый prepare при следующем open.
   */
  function resetAfterPromote(item: FileItem): void {
    clearSnapshot(item)
  }

  /**
   * Восстановить снимок из `POST document-sync-state` (ready / reconcile).
   * Явная `phase` в input перекрывает эвристику dsDirty → staged.
   */
  function restoreFromSyncState(
    item: FileItem,
    input: OfficePrepareSnapshotInput & { phase?: DsAttachmentPhase },
  ): DsAttachmentSnapshot {
    const snapshot = upsertFromPrepare(item, input)
    const phase = input.phase ?? (input.dsDirty ? 'staged' : 'idle')
    setPhase(item, phase)
    return { ...snapshot, phase }
  }

  return {
    getRowState,
    getSnapshot,
    getPhase,
    hasSnapshot,
    isOpenBlocked,
    pushPrevious,
    upsertFromPrepare,
    applyWsStaged,
    setPhase,
    clearSnapshot,
    clearAll,
    resetAfterPromote,
    restoreFromSyncState,
  }
}

export type DsAttachmentStore = ReturnType<typeof createDsAttachmentStore>

/** Store снимков по `elementId` FILEATTACHMENTS (singleton на время жизни элемента). */
const storesByElementId = new Map<number, DsAttachmentStore>()

/**
 * `documentSessionId` из полей строки или DS-снимка.
 * Используется в RPC policy, download, WS matching (R9).
 */
export function resolveAttachmentDocumentSessionId(
  item: FileItem,
  elementId?: number | null,
): string | null {
  const fields = (item.fields ?? {}) as Record<string, unknown>
  const fromFields = getAttachmentDocumentSessionIdFromFields(fields)
  if (fromFields) return fromFields

  const eid = elementId != null ? Number(elementId) : NaN
  if (Number.isFinite(eid) && eid > 0) {
    try {
      const fromSnapshot = String(
        getDsAttachmentStore(eid).getSnapshot(item)?.documentSessionId ?? '',
      ).trim()
      if (fromSnapshot) return fromSnapshot
    } catch {
      // ignore invalid elementId
    }
  }
  return null
}

/**
 * Записать в store полный DS-bundle из `GET /platform/download` (cache-hit / platform read).
 * Без `fullPrepareBundle` — no-op (только session/storage в row, не в store).
 */
export function upsertDsStoreFromOfficeStagingMeta(
  item: FileItem,
  elementId: number,
  meta: PlatformDownloadMeta,
): void {
  if (
    !meta.fullPrepareBundle
    || !meta.documentKey
    || !meta.documentUrl
    || !meta.callbackUrl
    || !meta.storageUri
    || !meta.docEditorInitConfig
  ) {
    return
  }

  const rowCv = resolveAttachmentRowCodeVersion((item.fields ?? {}) as Record<string, unknown>)
  const store = getDsAttachmentStore(elementId)
  const existing = store.getSnapshot(item)
  store.upsertFromPrepare(item, {
    documentKey: meta.documentKey,
    storageUri: meta.storageUri,
    documentUrl: meta.documentUrl,
    callbackUrl: meta.callbackUrl,
    documentSessionId: meta.documentSessionId,
    generation: meta.generation,
    token: meta.token,
    docEditorInitConfig: meta.docEditorInitConfig,
    dsDirty: meta.dsDirty === true || existing?.dsDirty === true,
    phase: resolvePhaseFromDownloadMeta(existing, meta),
    clientRowKey: existing?.clientRowKey || ensureAttachmentClientRowKey(item),
    platformCode: rowCv && rowCv.code > 0 ? rowCv.code : undefined,
    platformVersion: rowCv && rowCv.code > 0 ? rowCv.version : undefined,
  })
}

/** Lazy singleton store для `elementId`; бросает при невалидном id. */
export function getDsAttachmentStore(elementId: number): DsAttachmentStore {
  const eid = Number(elementId)
  if (!Number.isFinite(eid) || eid <= 0) {
    throw new Error('getDsAttachmentStore: invalid elementId')
  }
  let store = storesByElementId.get(eid)
  if (!store) {
    store = createDsAttachmentStore()
    storesByElementId.set(eid, store)
  }
  return store
}

/**
 * Сброс store при unmount FILEATTACHMENTS.
 * Иначе повторное открытие формы шлёт протухший `documentSessionId` в download/prepare.
 */
export function removeDsAttachmentStore(elementId: number): void {
  const eid = Number(elementId)
  if (!Number.isFinite(eid) || eid <= 0) return
  const store = storesByElementId.get(eid)
  if (!store) return
  store.clearAll()
  storesByElementId.delete(eid)
}

/** Тесты / смена формы: очистить все element stores. */
export function clearAllDsAttachmentStores(): void {
  for (const store of storesByElementId.values()) {
    store.clearAll()
  }
  storesByElementId.clear()
}

/**
 * Найти строку ленты для WS `OfficeCallbackHandler.statusChanged` по `documentSessionId`.
 */
export function resolveAttachmentRowForOfficeWsEvent(
  rows: FileItem[],
  documentSessionId: string | null,
  store: DsAttachmentStore,
): FileItem | null {
  const sessionId = String(documentSessionId ?? '').trim()
  if (!sessionId) return null
  return rows.find((row) => store.getSnapshot(row)?.documentSessionId === sessionId) ?? null
}
